import React, {
  useMemo,
} from 'react';

import {
  Stack,
  Typography,
} from '@mui/material';
import { useResizeDetector } from 'react-resize-detector';

import { LessonEnrolment } from '../../services/classes/types';
import { Student } from '../../services/students/types';
import useVisibleElementsCount from '../../utils/react/hooks/useVisibleElementsCount';
import HomeworkChip from '../HomeworkChip';
import ProfileIcon from '../ProfileIcon';

import { HomeworkRow, StudentProfileSection } from './index.styles';

interface StudentHomeworkRowProps {
  student: Student,
  lessonEnrolments: LessonEnrolment[],
  captionAttribute?: keyof Student,
  selectedClassLessonIdentifier: string,
  minCount?: number,
  maxCount?: number,
  editable?: boolean,
  markHomework: (lessonEnrolmentId: string, complete?: boolean) => void,
  offset?: number,
}

const StudentHomeworkRow = ({
  student,
  lessonEnrolments,
  captionAttribute,
  selectedClassLessonIdentifier,
  minCount = 1,
  maxCount = 28,
  editable = false,
  markHomework,
  offset = 0,
}: StudentHomeworkRowProps) => {
  const { width = 0, ref } = useResizeDetector();

  // 72px is the width of a single lesson chip and 8px is the spacing between them
  const displayedCount = useVisibleElementsCount(72 + 8, width, minCount, maxCount);

  const displayedLessonEnrolments = useMemo(() => {
    // Find the index of the selected lesson enrolment which will be displayed last
    const selectedLessonEnrolmentIndex = lessonEnrolments.findIndex(
      (lessonEnrolment) => lessonEnrolment.classLessonIdentifier === selectedClassLessonIdentifier,
    );

    // If the selected lesson enrolment is found, display the last `displayedCount`
    // lesson enrolments, with the chosen offset
    if (selectedLessonEnrolmentIndex !== -1) {
      return lessonEnrolments.slice(
        Math.max(0, selectedLessonEnrolmentIndex - displayedCount + offset),
        Math.max(0, selectedLessonEnrolmentIndex + 1 + offset),
      );
    }

    return undefined;
  }, [lessonEnrolments, selectedClassLessonIdentifier, displayedCount, offset]);

  return (
    <HomeworkRow>
      <StudentProfileSection>
        <ProfileIcon
          firstName={student.firstName}
          lastName={student.lastName}
          color={student.profileIcon?.color}
          imageUrl={student.profileIcon?.image}
          size={36}
        />
        <Stack direction="column">
          <Typography fontWeight="bold">
            {student.name}
          </Typography>
          <Typography>
            {student[captionAttribute as keyof Student] as string}
          </Typography>
        </Stack>
      </StudentProfileSection>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
        flexGrow={1}
        ref={ref}
      >
        {displayedLessonEnrolments?.map((lessonEnrolment) => (
          <HomeworkChip
            key={lessonEnrolment.id}
            lessonEnrolment={lessonEnrolment}
            onMark={(complete?: boolean) =>
              markHomework(lessonEnrolment.id, complete)}
            selected={lessonEnrolment.classLessonIdentifier === selectedClassLessonIdentifier}
            editable={editable}
          />
        ))}
      </Stack>
    </HomeworkRow>
  );
};

export default StudentHomeworkRow;
